import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { CompanyEventEnumType } from '__generated__/graphql';
import { useGoogleDrivePicker } from 'hooks/useGoogleDrivePicker';
import { FC, useRef, useState } from 'react';

import Button from 'primitives/Button';
import { Dialog, DialogActions, DialogPanel, DialogTitle } from 'primitives/Dialog';
import LoadingIndicator from 'primitives/LoadingIndicator';

import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';

import { InvestorRightsOptions } from './constants';

const CREATE_COMPANY_EVENT_MUTATION = gql(`#graphql
  mutation CreateCompanyEvent(
    $companyId: ID!,
    $label: String!,
    $type: CompanyEventEnumType!,
    $eventDate: String!,
    $isAuthorizationRequired: Boolean!,
    $description: String,
    $notes: String,
    $investorRightsAdded: [InvestorRightEnumType!],
    $investorRightsRemoved: [InvestorRightEnumType!],
    $shareAssets: [ID!],
    $deals: [ID!],
    $convertibleAssets: [ID!],
    $documents: [Upload!]
  ) {
    createCompanyEvent(
      companyId: $companyId,
      label: $label,
      type: $type,
      eventDate: $eventDate,
      isAuthorizationRequired: $isAuthorizationRequired,
      description: $description,
      notes: $notes,
      investorRightsAdded: $investorRightsAdded,
      investorRightsRemoved: $investorRightsRemoved,
      shareAssets: $shareAssets,
      deals: $deals,
      convertibleAssets: $convertibleAssets,
      documents: $documents
    ) {
      id
      label
      type
      eventDate
      isAuthorizationRequired
      authorizationStatus
      description
      notes
      investorRightsAdded
      investorRightsRemoved
    }
  }
`);

const GET_COMPANY_ASSETS = gql(`
  query GetCompanyAssets($companyId: ID!) {
    company(id: $companyId) {
      id
      shareClasses {
        id
        name
        shareAssets {
          id
          status
          numberOfShares
          deal {
            id
            schemeName
          }
        }
      }
      convertibleClasses {
        id
        name
        convertibleAssets {
          id
          status
          value
          deal {
            id
            schemeName
          }
        }
      }
      deals {
        id
        schemeName
      }
    }
  }
`);

const CreateCompanyEventDialogButton: FC<{
  companyId: string;
}> = ({ companyId }) => {
  const [showDialog, toggleDialog] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { googleDriveFiles, handleOpenPicker, downloadFiles } = useGoogleDrivePicker();

  const {
    data: assetsData,
    loading: assetsLoading,
    error: assetsError,
  } = useQuery(GET_COMPANY_ASSETS, {
    variables: { companyId },
  });

  const [createCompanyEvent, { loading, error }] = useMutation(CREATE_COMPANY_EVENT_MUTATION, {
    update(cache, { data }) {
      if (!data) return;
      cache.modify({
        fields: {
          events(existingCompanyEvents) {
            const newCompanyEventRef = cache.writeFragment({
              data: data.createCompanyEvent,
              fragment: gql(`
                fragment NewCompanyEventDialog on CompanyEventType {
                  id
                  label
                  type
                  eventDate
                  isAuthorizationRequired
                  authorizationStatus
                  description
                  notes
                  investorRightsAdded
                  investorRightsRemoved
                }
              `),
            });
            const updatedCompanyEvents = { ...existingCompanyEvents };
            updatedCompanyEvents.nodes = [newCompanyEventRef, ...updatedCompanyEvents.nodes];
            return updatedCompanyEvents;
          },
        },
      });
    },
    refetchQueries: ['fetchEventsByCompany'],
  });

  if (assetsLoading) return <LoadingIndicator />;
  if (assetsError) return <ErrorMessage />;

  const assets = assetsData?.company
    ? [
        ...(assetsData.company.shareClasses?.flatMap(sc =>
          sc.shareAssets.map(asset => ({
            id: asset.id,
            name: `${sc.name} - ${asset.numberOfShares} shares`,
            schemeName: asset.deal?.schemeName,
            type: 'SHARE_ASSET' as const,
          }))
        ) || []),
        ...(assetsData.company.convertibleClasses?.flatMap(cc =>
          cc.convertibleAssets.map(asset => ({
            id: asset.id,
            name: `${cc.name} - ${asset.value}`,
            schemeName: asset.deal?.schemeName,
            type: 'CONVERTIBLE_ASSET' as const,
          }))
        ) || []),
        ...(assetsData.company.deals
          ?.map(deal => {
            const allShareAndConvertibleAssets = [
              ...(assetsData.company.shareClasses?.flatMap(sc => sc.shareAssets) ?? []),
              ...(assetsData.company.convertibleClasses?.flatMap(cc => cc.convertibleAssets) ?? []),
            ];
            const doesDealHaveAsset = allShareAndConvertibleAssets.some(
              asset => asset?.deal?.id === deal.id
            );

            if (doesDealHaveAsset) return null;

            return {
              id: deal.id,
              name: 'Asset Not Created Yet',
              schemeName: deal.schemeName,
              type: 'DEAL' as const,
            };
          })
          .filter((item): item is NonNullable<typeof item> => item !== null) || []),
      ]
    : [];
  return (
    <>
      <Button onClick={() => toggleDialog(true)}>Create Event</Button>
      <Dialog open={showDialog} onClose={() => toggleDialog(false)}>
        <DialogPanel className="min-w-[600px]">
          <DialogTitle>Create an Event</DialogTitle>
          <FormPanel
            loading={loading}
            error={error}
            onCancel={() => toggleDialog(false)}
            onSubmit={async data => {
              try {
                const files = await downloadFiles();

                createCompanyEvent({
                  variables: {
                    companyId,
                    type: data.type,
                    eventDate: data.eventDate,
                    label: data.label,
                    description: data.description,
                    isAuthorizationRequired: data.isAuthorizationRequired || false,
                    notes: data.notes,
                    investorRightsAdded: data.investorRightsAdded,
                    investorRightsRemoved: data.investorRightsRemoved,
                    shareAssets:
                      data.assetIds.filter(
                        id => assets.find(asset => asset.id === id)?.type === 'SHARE_ASSET'
                      ) || [],
                    convertibleAssets:
                      data.assetIds.filter(
                        id => assets.find(asset => asset.id === id)?.type === 'CONVERTIBLE_ASSET'
                      ) || [],
                    deals:
                      data.assetIds.filter(
                        id => assets.find(asset => asset.id === id)?.type === 'DEAL'
                      ) || [],
                    documents: [...files, ...data.documents],
                  },
                }).then(() => toggleDialog(false));
              } catch (err) {}
            }}
            buttonRef={buttonRef}
          >
            <FormInput
              type="select"
              fieldName="type"
              label="Type"
              defaultValue={CompanyEventEnumType.TransactionalDocuments}
              options={[
                {
                  value: CompanyEventEnumType.TransactionalDocuments,
                  label: 'Transactional Documents',
                },
                { value: CompanyEventEnumType.AncillaryDocuments, label: 'Ancillary Documents' },
                { value: CompanyEventEnumType.RegulatoryDocuments, label: 'Regulatory Documents' },
                { value: CompanyEventEnumType.Others, label: 'Others' },
              ]}
            />
            <FormInput type="text" fieldName="label" label="Label" defaultValue="" />
            <FormInput type="date" fieldName="eventDate" label="Event Date" defaultValue="" />
            <FormInput type="text" fieldName="description" label="Description" defaultValue="" />
            <FormInput
              type="switch"
              fieldName="isAuthorizationRequired"
              label="Authorization Required"
              defaultValue={false}
            />
            <FormInput type="textarea" fieldName="notes" label="Notes" defaultValue="" />
            <FormInput
              type="multi_select"
              compact
              fieldName="investorRightsAdded"
              label="Investor Rights Added"
              defaultValue={[]}
              options={InvestorRightsOptions}
            />
            <FormInput
              type="multi_select"
              compact
              fieldName="investorRightsRemoved"
              label="Investor Rights Removed"
              defaultValue={[]}
              options={InvestorRightsOptions}
            />
            <FormInput type="upload" fieldName="documents" label="Documents" defaultValue={[]} />
            <Button className="col-span-full " onClick={handleOpenPicker}>
              {googleDriveFiles.length
                ? `${googleDriveFiles.length} file(s) selected`
                : 'Select from Google Drive'}
            </Button>
            <FormInput
              type="checkbox-group"
              fieldName="assetIds"
              label="Assets"
              compact
              defaultValue={''}
              options={assets.map(({ id, name, schemeName }) => ({
                value: id,
                label: `${name} (${schemeName || ''})`,
              }))}
              validators={{ required: true }}
            />
          </FormPanel>
          <DialogActions>
            <Button
              variant="secondary"
              onClick={() => {
                // @ts-ignore
                buttonRef.current?.cancel();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // @ts-ignore
                buttonRef.current?.submit();
              }}
              loading={loading}
            >
              Submit
            </Button>
          </DialogActions>
        </DialogPanel>
      </Dialog>
    </>
  );
};

export default CreateCompanyEventDialogButton;
